// FILE: src/pages/CarePlans/CarePlansPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { carePlansAPI, clientsAPI } from '../../api/services';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import '../../components/UI/UI.css';
import './CarePlans.css';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US',{year:'numeric',month:'short',day:'numeric'}) : '—';

export default function CarePlansPage() {
  const { hasRole } = useAuth();
  const canEdit     = hasRole('Admin','CaseManager');

  const [plans,     setPlans]     = useState([]);
  const [filtered,  setFiltered]  = useState([]);
  const [clients,   setClients]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [viewing,   setViewing]   = useState(null);
  const [deleting,  setDeleting]  = useState(null);
  const [delLoad,   setDelLoad]   = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [pRes, cRes] = await Promise.all([carePlansAPI.getAll(), clientsAPI.getAll()]);
      setPlans(pRes.data.data   || []);
      setFiltered(pRes.data.data || []);
      setClients(cRes.data.data  || []);
    } catch { toast.error('Failed to load care plans.'); }
    finally   { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!search.trim()) { setFiltered(plans); return; }
    const q = search.toLowerCase();
    setFiltered(plans.filter(p =>
      (p.ClientName||'').toLowerCase().includes(q) ||
      (p.PlanTitle||'').toLowerCase().includes(q)
    ));
  }, [search, plans]);

  const handleDelete = async () => {
    setDelLoad(true);
    try {
      await carePlansAPI.delete(deleting.ID);
      toast.success('Care plan deleted.');
      setDeleting(null); load();
    } catch { toast.error('Delete failed.'); }
    finally   { setDelLoad(false); }
  };

  const active = plans.filter(p=>p.IsActive).length;

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1>Care Plans</h1>
          <p>{plans.length} plans · {active} active</p>
        </div>
        <div style={{display:'flex',gap:10}}>
          <div className="search-bar">
            <span className="search-icon">🔍</span>
            <input className="form-control" placeholder="Search client or plan title…"
              value={search} onChange={e=>setSearch(e.target.value)}
              style={{paddingLeft:36,minWidth:240}}/>
          </div>
          {canEdit && (
            <button className="btn btn-primary" onClick={()=>{ setEditing(null); setShowModal(true); }}>➕ New Care Plan</button>
          )}
        </div>
      </div>

      <div className="stats-grid-3" style={{marginBottom:20}}>
        {[
          {label:'Total Plans', value:plans.length,              icon:'📋'},
          {label:'Active',      value:active,                     icon:'✅'},
          {label:'Inactive',    value:plans.length-active,        icon:'⏸️'},
        ].map(s=>(
          <div className="stat-card" key={s.label}>
            <div className="stat-card-header"><span className="stat-card-label">{s.label}</span><span style={{fontSize:20}}>{s.icon}</span></div>
            <div className="stat-card-value">{s.value}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="loading-state">⏳ Loading care plans…</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <div className="empty-state-text">{search ? 'No plans match.' : 'No care plans yet.'}</div>
          {!search && canEdit && (
            <button className="btn btn-primary" style={{marginTop:14}} onClick={()=>{ setEditing(null); setShowModal(true); }}>➕ Create First Plan</button>
          )}
        </div>
      ) : (
        <div className="careplan-grid">
          {filtered.map(p=>(
            <div key={p.ID} className={`careplan-card${p.IsActive?'':' inactive'}`}>
              <div className="careplan-card-header">
                <div>
                  <div className="careplan-client">{p.ClientName||'Unknown Client'}</div>
                  <div className="careplan-title">{p.PlanTitle||'Care Plan'}</div>
                </div>
                <span className={`badge ${p.IsActive?'badge-success':'badge-gray'}`}>
                  {p.IsActive?'Active':'Inactive'}
                </span>
              </div>
              <div className="careplan-dates">
                📅 {fmtDate(p.StartDate)} → {p.EndDate ? fmtDate(p.EndDate) : 'Ongoing'}
              </div>
              {p.Goals && (
                <div className="careplan-section">
                  <div className="careplan-section-label">Goals</div>
                  <div className="careplan-section-text">{p.Goals}</div>
                </div>
              )}
              {p.Notes && (
                <div className="careplan-section">
                  <div className="careplan-section-label">Notes</div>
                  <div className="careplan-section-text">{p.Notes}</div>
                </div>
              )}
              <div className="careplan-actions">
                <button className="btn btn-sm btn-secondary" onClick={()=>setViewing(p)}>👁️ View</button>
                {canEdit && (
                  <>
                    <button className="btn btn-sm btn-secondary" onClick={()=>{ setEditing(p); setShowModal(true); }}>✏️ Edit</button>
                    <button className="btn btn-sm btn-danger"    onClick={()=>setDeleting(p)}>🗑️</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <CarePlanFormModal plan={editing} clients={clients}
          onClose={()=>setShowModal(false)} onSaved={()=>{ setShowModal(false); load(); }}/>
      )}

      {viewing && (
        <div className="modal-overlay">
          <div className="modal modal-lg">
            <div className="modal-header">
              <span className="modal-title">📋 {viewing.PlanTitle||'Care Plan'}</span>
              <button className="modal-close" onClick={()=>setViewing(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:18}}>
                {[
                  {label:'Client',     value:viewing.ClientName||'—'},
                  {label:'Status',     value:viewing.IsActive?'Active':'Inactive'},
                  {label:'Start Date', value:fmtDate(viewing.StartDate)},
                  {label:'End Date',   value:viewing.EndDate?fmtDate(viewing.EndDate):'Ongoing'},
                ].map(f=>(
                  <div key={f.label}>
                    <div style={{fontSize:'0.72rem',fontWeight:700,color:'var(--gray-400)',textTransform:'uppercase',letterSpacing:'0.5px'}}>{f.label}</div>
                    <div style={{fontSize:'0.9rem',color:'var(--gray-800)',fontWeight:500,marginTop:3}}>{f.value}</div>
                  </div>
                ))}
              </div>
              {viewing.Goals && (
                <div style={{marginBottom:14}}>
                  <div style={{fontWeight:700,color:'var(--gray-700)',marginBottom:6}}>Goals</div>
                  <div style={{fontSize:'0.9rem',color:'var(--gray-600)',lineHeight:1.7,whiteSpace:'pre-wrap'}}>{viewing.Goals}</div>
                </div>
              )}
              {viewing.Interventions && (
                <div style={{marginBottom:14}}>
                  <div style={{fontWeight:700,color:'var(--gray-700)',marginBottom:6}}>Interventions</div>
                  <div style={{fontSize:'0.9rem',color:'var(--gray-600)',lineHeight:1.7,whiteSpace:'pre-wrap'}}>{viewing.Interventions}</div>
                </div>
              )}
              {viewing.Notes && (
                <div>
                  <div style={{fontWeight:700,color:'var(--gray-700)',marginBottom:6}}>Notes</div>
                  <div style={{fontSize:'0.9rem',color:'var(--gray-600)',lineHeight:1.7,whiteSpace:'pre-wrap'}}>{viewing.Notes}</div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setViewing(null)}>Close</button>
              {canEdit && <button className="btn btn-primary" onClick={()=>{ setEditing(viewing); setViewing(null); setShowModal(true); }}>✏️ Edit</button>}
            </div>
          </div>
        </div>
      )}

      {deleting && (
        <div className="modal-overlay">
          <div className="modal modal-sm">
            <div className="modal-header"><span className="modal-title">🗑️ Delete Care Plan</span><button className="modal-close" onClick={()=>setDeleting(null)}>✕</button></div>
            <div className="modal-body"><p style={{color:'var(--gray-600)'}}>Delete care plan for <strong>{deleting.ClientName}</strong>?</p></div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setDeleting(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={delLoad}>{delLoad?'Deleting…':'Yes, Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CarePlanFormModal({ plan, clients, onClose, onSaved }) {
  const isEdit = !!plan;
  const [form, setForm] = useState({
    ClientID:      plan?.ClientID      || '',
    PlanTitle:     plan?.PlanTitle     || '',
    StartDate:     plan?.StartDate     ? plan.StartDate.split('T')[0]    : '',
    EndDate:       plan?.EndDate       ? plan.EndDate.split('T')[0]      : '',
    Goals:         plan?.Goals         || '',
    Interventions: plan?.Interventions || '',
    Notes:         plan?.Notes         || '',
    IsActive:      plan?.IsActive !== undefined ? plan.IsActive : 1,
  });
  const [saving, setSaving] = useState(false);
  const set = (f,v) => setForm(p=>({...p,[f]:v}));

  const handleSave = async () => {
    if (!form.ClientID || !form.PlanTitle) { toast.error('Client and title are required.'); return; }
    setSaving(true);
    try {
      if (isEdit) { await carePlansAPI.update(plan.ID,form); toast.success('Care plan updated.'); }
      else        { await carePlansAPI.create(form);         toast.success('Care plan created.'); }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.message||'Save failed.'); }
    finally        { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <span className="modal-title">{isEdit?'✏️ Edit Care Plan':'📋 New Care Plan'}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Client <span className="req">*</span></label>
              <select className="form-control" value={form.ClientID} onChange={e=>set('ClientID',e.target.value)}>
                <option value="">— Select Client —</option>
                {clients.map(c=><option key={c.ID} value={c.ID}>{c.FirstName} {c.LastName}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Plan Title <span className="req">*</span></label>
              <input className="form-control" value={form.PlanTitle} onChange={e=>set('PlanTitle',e.target.value)} placeholder="e.g. Personal Care Plan 2025"/>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Start Date</label>
              <input className="form-control" type="date" value={form.StartDate} onChange={e=>set('StartDate',e.target.value)}/>
            </div>
            <div className="form-group">
              <label className="form-label">End Date</label>
              <input className="form-control" type="date" value={form.EndDate} onChange={e=>set('EndDate',e.target.value)}/>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Goals</label>
            <textarea className="form-control" rows={3} value={form.Goals} onChange={e=>set('Goals',e.target.value)} placeholder="Client's care goals…"/>
          </div>
          <div className="form-group">
            <label className="form-label">Interventions</label>
            <textarea className="form-control" rows={3} value={form.Interventions} onChange={e=>set('Interventions',e.target.value)} placeholder="Planned interventions and tasks…"/>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-control" rows={2} value={form.Notes} onChange={e=>set('Notes',e.target.value)} placeholder="Additional notes…"/>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-control" value={form.IsActive} onChange={e=>set('IsActive',parseInt(e.target.value))}>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving?'Saving…':isEdit?'💾 Update':'📋 Create Plan'}</button>
        </div>
      </div>
    </div>
  );
}